import { $t } from '@/i18n';
import { executeBundle, injectCSS } from '@tencent/jit-pack';
import { pick } from 'lodash';
import { message } from '@utils/wuji-message';
import { loadCss, loadScript } from '@/utils/common';
import { WUJI_DEPS_KEY } from '../../consts';
import { uuidv4 } from '@tencent/ui-core/lib/utils';
import eventBus, { EVENT } from '@utils/event-bus';
import presetLoader from '../preset-loader';
import { isExternalDeploy } from '@utils/external-deploy';
const WOKRER_STAGE = {
    INIT: 'init',
    LOAD: 'load',
};
/**
 * 使用jit-pack加载依赖
 *
 * 背后的原理是一个在浏览器里运行的npm和memfs
 */
class NpmLoader {
    modules = {};
    packageJson = '';
    loadedModules = new Set();
    bundleFile = {
        js: '',
        css: '',
    };
    presets = [];
    worker = null;
    loadPromiseResolveQueue = {};
    loadPromiseRejectQueue = {};
    flushQueueTimer = null;
    loadTaskQueue = [];
    constructor() {
        window.npmLoader = this;
    }
    async update(finalDependencies, newPacakgeJson) {
        if (finalDependencies.length === 0) {
            return {
                bundle: {
                    js: '',
                    css: '',
                },
                packageJson: '',
                presets: [],
            };
        }
        const allIncluded = finalDependencies.some(dependency => this.loadedModules.has(dependency));
        if (this.loadedModules.size === finalDependencies.length
            && allIncluded
            && this.packageJson === newPacakgeJson) {
            return {
                bundle: this.bundleFile,
                packageJson: this.packageJson,
                presets: this.presets,
            };
        }
        this.packageJson = newPacakgeJson || '';
        await this.load(finalDependencies, true);
        return {
            bundle: this.bundleFile,
            packageJson: this.packageJson,
            presets: this.presets,
        };
    }
    async init(bundlePath, dependencies, packageJson, presets) {
        if (presets) {
            await presetLoader.load(presets);
        }
        if (bundlePath?.js) {
            await loadScript(bundlePath.js);
        }
        if (bundlePath?.css) {
            await loadCss(bundlePath.css);
        }
        if (packageJson) {
            this.packageJson = packageJson;
        }
        let hasMissingModule = false;
        // 如果有没有加载到的依赖，加载它
        (dependencies || []).some((dependency) => {
            hasMissingModule = !Boolean(this.modules[dependency]);
            return hasMissingModule;
        });
        if (hasMissingModule) {
            await this.load(dependencies);
        }
    }
    register(vendorFunction) {
        const newVendor = vendorFunction();
        Object.keys(newVendor).forEach((k) => {
            newVendor[k] = {
                ...newVendor[k],
                __esModule: true,
            };
        });
        Object.assign(this.modules, newVendor);
        Object.assign(window[WUJI_DEPS_KEY], newVendor);
    }
    updateVersion(packageJson) {
        this.packageJson = packageJson;
        return this.load(Object.keys(this.modules), true);
    }
    flushQueue(uuid, needCleanup = false) {
        if (!needCleanup) {
            const needLoad = Array.from(this.loadedModules).filter(module => !this.modules[module]);
            // 如果所有的依赖都是已经加载过的，直接返回，不要重新构建了
            if (needLoad.length === 0) {
                const taskIndex = this.loadTaskQueue.findIndex(task => task.uuid === uuid);
                // 找到在这之前的所有请求，统一返回
                const resolvedTask = this.loadTaskQueue.splice(0, taskIndex + 1);
                resolvedTask.forEach((task) => {
                    const { uuid } = task;
                    this.loadPromiseResolveQueue[uuid](pick(this.modules));
                    delete this.loadPromiseResolveQueue[uuid];
                    delete this.loadPromiseRejectQueue[uuid];
                });
                return;
            }
        }
        this.worker?.postMessage({
            type: 'load',
            payload: {
                dependencies: Array.from(this.loadedModules),
                packageJson: this.packageJson || '',
                uuid,
                needCleanup,
            },
        });
    }
    /**
     * 外部有依赖请求时，将请求放入到一个队列里，
     * 如果0.5秒内没有新的请求，再加载依赖
     *
     * 原因：esbuild的onResolve钩子函数是遇到一个依赖就触发一次，所以在esbuild的钩子函数里触发load函数调用的话
     * 可能会重复触发多个load，所以做了这个类似debounce的操作
     * @param dependencies
     * @param needCleanup 是否需要cleanup - 如果为true，会删掉node_modules和package.json重新安装
     */
    async load(dependencies, needCleanup = false) {
        this.initWorker();
        if (!dependencies.length)
            return;
        const uuid = uuidv4();
        if (needCleanup) {
            this.loadedModules.clear();
        }
        dependencies.forEach(i => this.loadedModules.add(i));
        this.loadTaskQueue.push({
            uuid,
            timestamp: Date.now(),
        });
        if (this.flushQueueTimer) {
            clearTimeout(this.flushQueueTimer);
        }
        this.flushQueueTimer = setTimeout(() => this.flushQueue.bind(this)(uuid, needCleanup), 500);
        return new Promise((resolve, reject) => {
            this.loadPromiseResolveQueue[uuid] = resolve;
            this.loadPromiseRejectQueue[uuid] = reject;
        });
    }
    async executeBundle(bundleFiles) {
        await executeBundle(bundleFiles.js);
        if (bundleFiles.css) {
            injectCSS(bundleFiles.css);
        }
    }
    /**
     * 加载成功后的回调函数
     */
    async handleWorkerSuccess(payload) {
        const { bundledFiles, uuid, packageJson, presets } = payload;
        const taskIndex = this.loadTaskQueue.findIndex(task => task.uuid === uuid);
        // 找到在这之前的所有请求，统一返回
        const resolvedTask = this.loadTaskQueue.splice(0, taskIndex + 1);
        this.bundleFile = bundledFiles;
        this.packageJson = packageJson;
        this.presets = presets || [];
        await this.executeBundle(this.bundleFile);
        resolvedTask.forEach((task) => {
            const { uuid } = task;
            this.loadPromiseResolveQueue[uuid](pick(this.modules));
            delete this.loadPromiseResolveQueue[uuid];
            delete this.loadPromiseRejectQueue[uuid];
        });
    }
    handleWorkerError(error, uuid) {
        const taskIndex = this.loadTaskQueue.findIndex(task => task.uuid === uuid);
        const resolvedTask = this.loadTaskQueue.splice(0, taskIndex + 1);
        this.loadedModules = new Set();
        resolvedTask.forEach((task) => {
            const { uuid } = task;
            this.loadPromiseRejectQueue[uuid](error);
            delete this.loadPromiseResolveQueue[uuid];
            delete this.loadPromiseRejectQueue[uuid];
        });
    }
    updateProgress(progress) {
        eventBus.emit(EVENT.updateDepLoaderState, progress);
    }
    debug(moduleName) {
        this.worker?.postMessage({
            type: 'debug',
            payload: {
                moduleName,
            },
        });
    }
    async initWorker() {
        if (this.worker)
            return;
        try {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const NpmWorker = await import('./npm.worker').then((module) => module.default);
            this.worker = new NpmWorker();
            this.worker.addEventListener('message', (event) => {
                const { type, payload } = event.data;
                switch (type) {
                    case 'ready':
                        console.log('Dependency worker is ready');
                        break;
                    case 'success':
                        // Worker 加载完成后的处理
                        this.handleWorkerSuccess(payload);
                        break;
                    case 'error':
                        if (payload.stage === WOKRER_STAGE.INIT) {
                            message.error($t('k_c6ccb06f', { arg_0: payload.error }));
                            return;
                        }
                        this.handleWorkerError(payload.error, payload.uuid);
                        break;
                    case 'progress':
                        this.updateProgress(payload);
                        break;
                }
            });
            this.worker.addEventListener('error', (error) => {
                console.error('Worker error:', error);
            });
            // 初始化 Worker
            this.worker.postMessage({ type: 'init',
                payload: {
                    registry: isExternalDeploy() ? '' : 'https://mirrors.tencent.com/npm/',
                } });
        }
        catch (error) {
            console.error('Failed to create worker:', error);
            this.worker = null;
        }
    }
}
export default NpmLoader;
